/**
 * Click/shift-click rectangle-selection for the merged table's availability
 * grid (offer-unified-table, CU-86cbc3r8b) — the click half of
 * `use-offer-table-multi-select.ts`'s multi-select seam; see that file's
 * own doc comment for why click and drag are split apart.
 */
import * as React from 'react';
import {
  computeMultiSelectRectangle,
  isShiftSelectableState,
  type AvailabilityGridData,
  type MultiCellSelection,
  type InventoryAvailabilityPeriod,
} from '@ooh/screens';

export interface UseMultiSelectClickOptions {
  canSelect: boolean;
  multiSelection: MultiCellSelection | null;
  gridRows: { id: string; label: string }[];
  periods: InventoryAvailabilityPeriod[];
  grid: AvailabilityGridData;
  updateMultiSelectAnchor: (selection: MultiCellSelection | null) => void;
}

export function useMultiSelectClick({
  canSelect,
  multiSelection,
  gridRows,
  periods,
  grid,
  updateMultiSelectAnchor,
}: UseMultiSelectClickOptions): (
  rowId: string,
  period: InventoryAvailabilityPeriod,
  shiftKey: boolean,
) => void {
  return React.useCallback(
    (rowId: string, period: InventoryAvailabilityPeriod, shiftKey: boolean) => {
      if (!canSelect) return;
      if (shiftKey && multiSelection?.anchor) {
        const cells = computeMultiSelectRectangle(gridRows, periods, grid, multiSelection.anchor, {
          unitId: rowId,
          periodId: period.id,
        });
        updateMultiSelectAnchor({ cells, anchor: { unitId: rowId, periodId: period.id } });
        return;
      }
      const state = grid[rowId]?.[period.id] ?? 'free';
      if (!isShiftSelectableState(state)) return;
      updateMultiSelectAnchor({
        cells: [{ unitId: rowId, periodId: period.id }],
        anchor: { unitId: rowId, periodId: period.id },
      });
    },
    [canSelect, multiSelection, gridRows, periods, grid, updateMultiSelectAnchor],
  );
}
